import { Modal, Spinner } from "@cntrl/ui";
import { useBandContextOrNull } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useBandTeamOrNull } from "../../app/hooks/useBandTeam.js";
import { BandProfileBrowser } from "./BandProfileModal.js";
import { CrewProfileModal } from "./CrewProfileModal.js";
import styles from "./CrewProfileModal.module.scss";

/**
 * "View my profile" — the caller's OWN profile, in place, no route change (PO, 2026-08-20; the routed
 * `/profile` stays for bookmarks). Opened from every shell's account menu: the artist band switcher, the
 * roster account chip and the org chip.
 *
 * The OWNER's profile IS the band profile (the Artist IS the band — PO, 2026-08-20): an owner seat gets the
 * band profile modal, not a person profile. Everyone else gets `CrewProfileModal` — the SAME dialog a crew
 * row, a band-profile member row and the org Team list open, which works out for itself that this is you and
 * shows the Edit pencil. This route decides WHICH profile, never how it renders.
 *
 * **A caller with no band seat is the ORDINARY case here now, not a fallback** (PO, 2026-09-01). It used to
 * mean "an org staffer working through the artist lens", and it dropped them to the plain person-record
 * EDITOR — a 520px form where a band member got an identity rail. But an org member's profile is a profile:
 * the same dialog, with the organizations they sit in and the artists they reach on the rail, and the same
 * pencil to edit it. `CrewProfileModal` renders with no band mounted at all, so this needs no branch for it
 * — the seat lookup simply finds nothing, exactly as it does for a friend band's member.
 */
export function MyProfileModal({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const bandId = useBandContextOrNull()?.bandId;
  const team = useBandTeamOrNull();

  const myPersonId = me.data?.person?.personId;
  const seat = team.items.find((s) => s.personId === myPersonId);

  if (me.isLoading || team.isLoading) {
    return (
      <Modal title="Your profile" onClose={onClose} closeLabel="Close profile" size="xl" hideHeader>
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={22} label="Loading your profile…" />
        </div>
      </Modal>
    );
  }

  if (seat?.isOwner && bandId) return <BandProfileBrowser initialBandId={bandId} onClose={onClose} />;

  // No seat, or a non-owner one — either way the person dialog, addressed by the caller's own id. `me`
  // resolving to nothing is not a state this can render: the shell above it is already authenticated.
  if (!myPersonId) return null;
  return <CrewProfileModal personId={myPersonId} onClose={onClose} />;
}
